import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calculator from './Calculator';
import QuestionNavigator from './QuestionNavigator';
import { useKeyboardShortcuts, optionLetter, SHORTCUT_HINTS } from '../utils/keyboardShortcuts';
import './ExamMode.css';
import './PracticeMode.css';

const SECTION_NAMES = {
  DM: 'Decision Making',
  VR: 'Verbal Reasoning',
  QR: 'Quantitative Reasoning',
  SJT: 'Situational Judgement',
};

// Human-readable labels for the MedEntry-style question_type taxonomy
// computed server-side (see backfillQuestionTypes in ensureSchema.js).
// Distinct from `family`, which is the raw subject/scenario tag (e.g. a VR
// passage's subject domain) rather than a question format/category.
const TYPE_LABELS = {
  logical_puzzles: 'Logical Puzzles',
  syllogisms: 'Syllogisms',
  interpreting_information: 'Interpreting Information',
  recognising_assumptions: 'Recognising Assumptions',
  venn_diagrams: 'Venn Diagrams',
  probabilistic_reasoning: 'Probabilistic Reasoning',
  percentages: 'Percentages',
  proportionality: 'Proportionality',
  rates: 'Rates',
  averages: 'Averages',
  other: 'Other (Ratios, Geometry, Data Interpretation)',
  true_false_cant_tell: "True / False / Can't Tell",
  reading_comprehension: 'Reading Comprehension',
  appropriateness: 'Appropriateness',
  importance: 'Importance',
  most_least_appropriate: 'Most/Least Appropriate',
};

const DIFFICULTY_LABELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

function PracticeMode({ section, user, onBack, onShowResults, apiUrl, initialFilters }) {
  const isStaff = user?.role === 'admin' || user?.role === 'tutor';
  const [topics, setTopics] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null); // null = not chosen yet, 'ALL' = no filter
  // Multi-select filter state — a student can combine several topics,
  // question types and difficulties in one practice session. `topics` now
  // holds display groups (see backend FAMILY_DISPLAY_GROUPS) each carrying
  // the underlying raw `family` values it represents, so selecting e.g.
  // "Venn Diagrams" expands to every fam_venn* variant under the hood.
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(initialFilters?.question_type ? [initialFilters.question_type] : []);
  const [selectedDifficulties, setSelectedDifficulties] = useState(initialFilters?.difficulty ? [initialFilters.difficulty] : []);
  const [topicsLoading, setTopicsLoading] = useState(true);

  const filterFamily = selectedGroups
    .map((g) => topics.find((t) => t.group === g))
    .filter(Boolean)
    .flatMap((t) => t.families)
    .join(',');
  const filterType = selectedTypes.join(',');
  const filterDifficulty = selectedDifficulties.join(',');

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  // In-progress picks for 'five_statement' questions, keyed by question id.
  // `answers[id]` only gets populated once all five are picked (see
  // handleFiveStatementSelect), so the existing isAnswered/Next-button/
  // save logic elsewhere needs no changes for this format.
  const [fiveStatementDraft, setFiveStatementDraft] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptId, setAttemptId] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [pendingResume, setPendingResume] = useState(null); // active attempt found, awaiting user choice

  useEffect(() => {
    const fetchTopicsAndTypes = async () => {
      try {
        setTopicsLoading(true);
        const [topicsRes, typesRes] = await Promise.all([
          axios.get(`${apiUrl}/practice/topics/${section}`),
          axios.get(`${apiUrl}/practice/types/${section}`),
        ]);
        setTopics(topicsRes.data);
        setQuestionTypes(typesRes.data);
      } catch (err) {
        setTopics([]);
        setQuestionTypes([]);
      } finally {
        setTopicsLoading(false);
      }
    };
    fetchTopicsAndTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const topicFamilyParam = (topic) => (topic && topic !== 'ALL' ? topic : '');

  // question_type/difficulty filters (e.g. launched from a "Try Yourself"
  // link in the Excellence Program) are treated as one-off targeted
  // drills, not tracked by the resume system — practice_attempts.
  // topic_family is a single column that predates this multi-dimensional
  // filtering, so extending resume-tracking to every filter combination
  // isn't worth the schema complexity for what's meant to be a quick,
  // specific practice burst rather than an ongoing single-topic session.
  const isFilteredDrill = Boolean(filterType || filterDifficulty);

  const fetchQuestions = async (topic, resumeFrom) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '10000' });
      const family = topicFamilyParam(topic);
      if (family) params.set('family', family);
      if (filterType) params.set('question_type', filterType);
      if (filterDifficulty) params.set('difficulty', filterDifficulty);
      const response = await axios.get(`${apiUrl}/practice/questions/${section}?${params.toString()}`);
      setQuestions(response.data.questions);

      if (resumeFrom) {
        setAttemptId(resumeFrom.id);
        setAnswers(resumeFrom.saved_answers || {});
        setCurrentIndex(Math.min(resumeFrom.current_question_index || 0, response.data.questions.length - 1));
      } else if (!isStaff) {
        // Staff are browsing for reference, not attempting — no attempt
        // record, no progress tracking, no resume prompt.
        const attemptResponse = await axios.post(`${apiUrl}/practice/attempts`, {
          section,
          topic_family: topicFamilyParam(topic) || null,
        });
        setAttemptId(attemptResponse.data.id);
      }
    } catch (err) {
      setError('Failed to load questions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChooseTopic = async (topic) => {
    setSelectedTopic(topic);
    if (isStaff || isFilteredDrill) {
      fetchQuestions(topic);
      return;
    }
    try {
      const params = new URLSearchParams({ section });
      const family = topicFamilyParam(topic);
      if (family) params.set('topic_family', family);
      const response = await axios.get(`${apiUrl}/practice/attempts/active?${params.toString()}`);
      if (response.data) {
        setPendingResume({ topic, attempt: response.data });
        return;
      }
    } catch (err) {
      // fall through to starting fresh
    }
    fetchQuestions(topic);
  };

  // Auto-launch straight into filtered practice when arriving from a
  // "Try Yourself" deep-link (initialFilters set) — skips the manual
  // picker screen entirely for a one-click hand-off from curriculum
  // content into targeted practice.
  useEffect(() => {
    if (initialFilters && (initialFilters.question_type || initialFilters.difficulty || initialFilters.family)) {
      // Deep-links pass an exact raw family value (e.g. fam_venn_visual for
      // "practice just the new visual Venn diagrams"), so this bypasses the
      // group-based multi-select entirely and goes straight to the backend.
      handleChooseTopic(initialFilters.family || 'ALL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResume = () => {
    const { topic, attempt } = pendingResume;
    setPendingResume(null);
    fetchQuestions(topic, attempt);
  };

  const handleStartFresh = () => {
    const { topic } = pendingResume;
    setPendingResume(null);
    fetchQuestions(topic);
  };

  // Debounced progress save whenever the student answers or navigates.
  useEffect(() => {
    if (!attemptId) return;
    const handle = setTimeout(() => {
      axios.patch(`${apiUrl}/practice/attempts/${attemptId}/progress`, {
        current_question_index: currentIndex,
        saved_answers: answers,
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, currentIndex, answers]);

  const saveAnswer = async (question, answer) => {
    if (!attemptId) return; // staff browsing — nothing to save
    try {
      await axios.post(`${apiUrl}/practice/answers`, {
        attempt_id: attemptId,
        question_id: question.id,
        selected_answer: answer,
        time_spent_seconds: 0,
      });
    } catch (err) {
      console.error('Failed to save answer:', err);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSelectByLetter = (letter) => {
    const opts = getOptions(questions[currentIndex]);
    const match = opts.find((o) => optionLetter(o) === letter);
    if (match) handleAnswerSelect(questions[currentIndex].id, match);
  };

  const handleToggleFlag = () => {
    const q = questions[currentIndex];
    if (!q) return;
    setFlagged((prev) => ({ ...prev, [q.id]: !prev[q.id] }));
  };

  const goToIndex = (idx) => {
    setShowExplanation(false);
    setCurrentIndex(idx);
  };

  const handleNext = () => {
    const currentQuestion = questions[currentIndex];
    if (!isStaff) {
      if (!answers[currentQuestion.id]) return;
      saveAnswer(currentQuestion, answers[currentQuestion.id]);
    }
    if (currentIndex < questions.length - 1) goToIndex(currentIndex + 1);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) goToIndex(currentIndex - 1);
  };

  const handleFinishEarly = () => {
    if (window.confirm('Finish this practice session now? You can review your answers afterwards, but this closes the session — any unanswered questions stay unanswered.')) {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    const currentQuestion = questions[currentIndex];
    if (currentQuestion && answers[currentQuestion.id]) {
      await saveAnswer(currentQuestion, answers[currentQuestion.id]);
    }

    try {
      const response = await axios.post(`${apiUrl}/practice/attempts/${attemptId}/complete`, { duration_seconds: 0 });
      onShowResults({
        type: 'practice',
        section,
        attempt: response.data,
        totalQuestions: questions.length,
        answeredCount: Object.keys(answers).length,
      });
    } catch (err) {
      console.error('Failed to complete attempt:', err);
      setError('Failed to submit practice session');
    }
  };

  const getOptions = (question) => {
    if (!question || !question.options) return [];
    return typeof question.options === 'string' ? JSON.parse(question.options) : question.options;
  };

  // For 'five_statement' questions: the five in-progress Yes/No picks,
  // preferring the saved (complete) answer once it exists.
  const getFiveStatementValues = (question) => {
    if (!question) return [];
    if (answers[question.id]) {
      try {
        return JSON.parse(answers[question.id]);
      } catch {
        return [];
      }
    }
    return fiveStatementDraft[question.id] || [];
  };

  const handleFiveStatementSelect = (question, idx, value) => {
    const optsLen = getOptions(question).length;
    const current = getFiveStatementValues(question);
    const updated = current.length === optsLen ? [...current] : new Array(optsLen).fill(null);
    updated[idx] = value;
    setFiveStatementDraft((prev) => ({ ...prev, [question.id]: updated }));
    if (updated.every((v) => v === 'Yes' || v === 'No')) {
      setAnswers((prev) => ({ ...prev, [question.id]: JSON.stringify(updated) }));
    }
  };

  const currentQuestion = questions[currentIndex];
  const currentOptions = getOptions(currentQuestion);
  const isAnswered = currentQuestion && answers[currentQuestion.id];

  useKeyboardShortcuts({
    enabled: Boolean(currentQuestion) && !showCalculator && !showNavigator,
    onNext: handleNext,
    onPrevious: handlePrevious,
    onFlag: handleToggleFlag,
    onToggleCalculator: () => setShowCalculator((v) => !v),
    onToggleNavigator: () => setShowNavigator((v) => !v),
    onSelectOption: handleSelectByLetter,
    optionLetters: currentQuestion?.answer_format === 'five_statement' ? [] : currentOptions.map(optionLetter).filter(Boolean),
  });

  // ---- Topic picker / filter screen ----
  if (selectedTopic === null) {
    // Estimate how many questions match the current combination of
    // selected types/difficulties (topic selection isn't broken down by
    // difficulty server-side per-group, so this estimate covers type +
    // difficulty only — the actual count is shown once practice starts).
    const matchingTypeStats = selectedTypes.length
      ? questionTypes.filter((t) => selectedTypes.includes(t.question_type))
      : questionTypes;
    const filteredCount = (selectedTypes.length || selectedDifficulties.length)
      ? matchingTypeStats.reduce((sum, t) => {
          if (selectedDifficulties.length) {
            return sum + selectedDifficulties.reduce((s, d) => s + (t[d] || 0), 0);
          }
          return sum + t.total;
        }, 0)
      : null;
    const noFiltersActive = selectedGroups.length === 0 && selectedTypes.length === 0 && selectedDifficulties.length === 0;

    return (
      <div className="container practice-mode">
        <div className="header">
          <div>
            <h1>Practice Mode - {SECTION_NAMES[section] || section}</h1>
            <p className="header-subtitle">Combine as many topics, question types and difficulties as you like, or just start with everything</p>
          </div>
          <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        </div>
        <div className="content">
          {topicsLoading ? (
            <div className="loading"><div className="spinner"></div>Loading topics...</div>
          ) : (
            <>
              <div className="practice-filters">
                <div className="filter-field">
                  <label>Topic</label>
                  <MultiSelectDropdown
                    placeholder="All topics"
                    selected={selectedGroups}
                    onChange={setSelectedGroups}
                    options={topics.map((t) => ({ value: t.group, label: `${t.group} (${t.count})` }))}
                  />
                </div>

                {questionTypes.length > 0 && (
                  <div className="filter-field">
                    <label>Question type</label>
                    <MultiSelectDropdown
                      placeholder="All types"
                      selected={selectedTypes}
                      onChange={setSelectedTypes}
                      options={questionTypes.map((t) => ({ value: t.question_type, label: `${TYPE_LABELS[t.question_type] || t.question_type} (${t.total})` }))}
                    />
                  </div>
                )}

                <div className="filter-field">
                  <label>Difficulty</label>
                  <MultiSelectDropdown
                    placeholder="All difficulties"
                    selected={selectedDifficulties}
                    onChange={setSelectedDifficulties}
                    options={Object.entries(DIFFICULTY_LABELS).map(([value, label]) => ({ value, label }))}
                  />
                </div>
              </div>

              {filteredCount !== null && (
                <p className="filter-summary">{filteredCount} question(s) match this filter</p>
              )}

              <div className="practice-filters-actions">
                <button className="btn btn-primary btn-lg" onClick={() => handleChooseTopic(filterFamily || 'ALL')}>
                  Start Practice
                </button>
              </div>

              {noFiltersActive && (
                <div className="topic-grid">
                  {topics.map((t) => (
                    <button key={t.group} className="topic-card" onClick={() => handleChooseTopic(t.families.join(','))}>
                      <div className="topic-name">{t.group}</div>
                      <div className="topic-count">{t.count} questions</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (pendingResume) {
    const attempt = pendingResume.attempt;
    return (
      <div className="container practice-mode">
        <div className="header">
          <div><h1>Practice Mode - {SECTION_NAMES[section] || section}</h1></div>
          <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        </div>
        <div className="content">
          <div className="exam-intro">
            <h2>You have an unfinished session here</h2>
            <div className="exam-details">
              <p>You were on question <strong>{(attempt.current_question_index || 0) + 1}</strong>, with{' '}
                <strong>{Object.keys(attempt.saved_answers || {}).length}</strong> question(s) answered so far.</p>
            </div>
            <div className="exam-actions">
              <button className="btn btn-secondary" onClick={handleStartFresh}>Start New</button>
              <button className="btn btn-primary btn-lg" onClick={handleResume}>Resume</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="header"><h1>Practice Mode - {section}</h1></div>
        <div className="content"><div className="loading"><div className="spinner"></div>Loading questions...</div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header"><h1>Practice Mode - {section}</h1></div>
        <div className="content">
          <div className="error">{error}</div>
          <button className="btn btn-primary" onClick={onBack}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="container">
        <div className="header"><h1>Practice Mode - {section}</h1></div>
        <div className="content">
          <div className="error">No questions available for this topic yet.</div>
          <button className="btn btn-primary" onClick={onBack}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const isFlagged = currentQuestion && flagged[currentQuestion.id];

  return (
    <div className="container practice-mode">
      <div className="header">
        <div>
          <h1>{isStaff ? 'Question Bank – ' : 'Practice Mode - '}{section}</h1>
          <p className="header-subtitle">
            {describeActiveSelection(selectedTopic, selectedGroups, selectedTypes, selectedDifficulties)}
            {isStaff ? ' · browsing, not tracked as an attempt' : ''}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
      </div>

      <div className="content">
        <div className="practice-progress">
          <div className="progress-info">Question {currentIndex + 1} of {questions.length}</div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}></div>
          </div>
        </div>

        <div className="practice-toolbar">
          <button className="toolbar-btn" onClick={() => setShowCalculator(true)}>🧮 Calculator</button>
          <button className="toolbar-btn" onClick={() => setShowNavigator(true)}>🗺️ Navigator</button>
          <button className={`toolbar-btn ${isFlagged ? 'toolbar-btn-active' : ''}`} onClick={handleToggleFlag}>
            {isFlagged ? '🚩 Flagged' : '🏳️ Flag question'}
          </button>
          <button className="toolbar-btn" onClick={() => setShowHints((v) => !v)}>⌨️ Shortcuts</button>
          {!isStaff && (
            <button className="toolbar-btn toolbar-btn-finish" onClick={handleFinishEarly}>✔ Finish Attempt</button>
          )}
        </div>

        {showHints && (
          <div className="shortcut-hints">
            {SHORTCUT_HINTS.map((h) => (
              <span key={h.key}><strong>{h.key}</strong> {h.action}</span>
            ))}
          </div>
        )}

        <div className="question-container">
          {currentQuestion.diagram_svg && (
            <div
              className="question-diagram"
              // Trusted, server-authored SVG only (our own generated
              // diagrams) — never rendered for user-submitted content.
              dangerouslySetInnerHTML={{ __html: currentQuestion.diagram_svg }}
            />
          )}
          {currentQuestion.passage_text && (
            <div className="passage-box">
              {currentQuestion.passage_title && <h4>{currentQuestion.passage_title}</h4>}
              <p>{currentQuestion.passage_text}</p>
            </div>
          )}
          {currentQuestion.scenario_text && (
            <div className="passage-box">
              {currentQuestion.scenario_title && <h4>{currentQuestion.scenario_title}</h4>}
              <p>{currentQuestion.scenario_text}</p>
            </div>
          )}

          {currentQuestion.difficulty && (
            <span className={`difficulty-badge difficulty-${currentQuestion.difficulty.toLowerCase()}`}>
              {currentQuestion.difficulty}
            </span>
          )}

          <h3 className="question-text">{currentQuestion.question_text}</h3>

          {currentQuestion.answer_format === 'five_statement' ? (
            <div className="five-statement-set">
              {(() => {
                let correctValues = [];
                try {
                  correctValues = JSON.parse(currentQuestion.correct_answer || '[]');
                } catch {
                  correctValues = [];
                }
                const selectedValues = getFiveStatementValues(currentQuestion);
                return currentOptions.map((statementText, idx) => {
                  const selected = selectedValues[idx] || null;
                  const correctVal = correctValues[idx];
                  return (
                    <div key={idx} className="five-statement-row">
                      <div className="five-statement-text">{statementText}</div>
                      <div className="five-statement-buttons">
                        {['Yes', 'No'].map((val) => {
                          const isSel = selected === val;
                          const isCorrectVal = showExplanation && val === correctVal;
                          const isWrongSel = showExplanation && isSel && val !== correctVal;
                          return (
                            <button
                              key={val}
                              type="button"
                              className={[
                                'five-statement-btn',
                                isSel && !showExplanation ? 'selected' : '',
                                isCorrectVal ? 'correct' : '',
                                isWrongSel ? 'incorrect' : '',
                              ].join(' ').trim()}
                              disabled={showExplanation}
                              onClick={() => handleFiveStatementSelect(currentQuestion, idx, val)}
                            >
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="options">
              {currentOptions.map((option, idx) => {
                const isSelected = isAnswered === option;
                const isCorrectOption = showExplanation && option === currentQuestion.correct_answer;
                const isWrongSelected = showExplanation && isSelected && option !== currentQuestion.correct_answer;
                return (
                  <div
                    key={idx}
                    className={[
                      'option',
                      isSelected && !showExplanation ? 'selected' : '',
                      isCorrectOption ? 'correct' : '',
                      isWrongSelected ? 'incorrect' : '',
                    ].join(' ').trim()}
                    onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                  >
                    {option}
                  </div>
                );
              })}
            </div>
          )}

          {isStaff && !showExplanation && (
            <button className="btn btn-primary explain-btn" onClick={() => setShowExplanation(true)}>
              See Answer
            </button>
          )}
          {!isStaff && isAnswered && !showExplanation && (
            <button className="btn btn-primary explain-btn" onClick={() => setShowExplanation(true)}>
              Explain Answer
            </button>
          )}

          {showExplanation && (
            <div className="explanation">
              {currentQuestion.answer_format === 'five_statement' ? (
                <>
                  <strong>Correct answers shown above (green = correct, red = your incorrect pick).</strong>
                  <br />
                </>
              ) : (
                <>
                  <strong>Correct answer:</strong> {currentQuestion.correct_answer}
                  <br />
                </>
              )}
              <strong>Explanation:</strong> {currentQuestion.explanation}
            </div>
          )}
        </div>

        <div className="practice-navigation">
          <button className="btn btn-secondary" onClick={handlePrevious} disabled={currentIndex === 0}>
            ← Previous
          </button>

          <div className="nav-info">
            {isStaff ? `Question ${currentIndex + 1} of ${questions.length}` : `Answered: ${Object.keys(answers).length} / ${questions.length}`}
          </div>

          {isStaff ? (
            <button className="btn btn-primary" onClick={handleNext} disabled={currentIndex === questions.length - 1}>
              Next →
            </button>
          ) : currentIndex < questions.length - 1 ? (
            <button className="btn btn-primary" onClick={handleNext} disabled={!isAnswered}>
              Next →
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleFinish}>
              Finish Practice
            </button>
          )}
        </div>
      </div>

      {showCalculator && <Calculator onClose={() => setShowCalculator(false)} />}
      {showNavigator && (
        <QuestionNavigator
          questions={questions}
          currentIndex={currentIndex}
          answers={answers}
          flagged={flagged}
          onJump={goToIndex}
          onClose={() => setShowNavigator(false)}
        />
      )}
    </div>
  );
}

function formatTopicName(family) {
  return (family || '')
    .replace(/^fam_/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Short summary shown in the practice-screen header once a session has
// started. Prefers the multi-select state (set when a student built the
// session from the filter dropdowns); falls back to formatting the raw
// family string for deep-links, which bypass the multi-select entirely.
function describeActiveSelection(selectedTopic, selectedGroups, selectedTypes, selectedDifficulties) {
  const parts = [];
  if (selectedGroups.length) {
    parts.push(selectedGroups.join(', '));
  } else if (selectedTopic && selectedTopic !== 'ALL') {
    const families = selectedTopic.split(',');
    parts.push(families.length > 1 ? `${formatTopicName(families[0])} (+${families.length - 1} more)` : formatTopicName(families[0]));
  }
  if (selectedTypes.length) parts.push(selectedTypes.map((t) => TYPE_LABELS[t] || t).join(', '));
  if (selectedDifficulties.length) parts.push(selectedDifficulties.map((d) => DIFFICULTY_LABELS[d] || d).join(', '));
  return parts.length ? parts.join(' · ') : 'All topics';
}

// Lightweight multi-select: a button showing a summary of the current
// selection, opening a checkbox list on click. Used for Topic/Type/
// Difficulty so a student can combine several of each in one session.
function MultiSelectDropdown({ placeholder, options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleValue = (value) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <div className="multiselect" ref={containerRef}>
      <button type="button" className="multiselect-trigger" onClick={() => setOpen((v) => !v)}>
        <span>{summary}</span>
        <span className="multiselect-caret">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="multiselect-panel">
          {selected.length > 0 && (
            <button type="button" className="multiselect-clear" onClick={() => onChange([])}>
              Clear selection
            </button>
          )}
          {options.map((opt) => (
            <label key={opt.value} className="multiselect-option">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default PracticeMode;
